/** Text transforms for Edit menu (buffer subset, §16.2). */

function isWordChar(c: string): boolean {
  return /[0-9A-Za-z_]/.test(c);
}

/** Word at offset: ASCII identifier around caret when selection empty. */
export function wordBoundsAt(text: string, offset: number): { start: number; end: number } | null {
  if (offset < 0 || offset > text.length) return null;
  let i = offset === text.length && offset > 0 ? offset - 1 : offset;
  if (i >= 0 && i < text.length && !isWordChar(text[i]!) && i > 0 && isWordChar(text[i - 1]!)) i--;
  if (i < 0 || i >= text.length || !isWordChar(text[i]!)) return null;
  let s = i;
  while (s > 0 && isWordChar(text[s - 1]!)) s--;
  let e = i;
  while (e < text.length && isWordChar(text[e]!)) e++;
  return { start: s, end: e };
}

export function transformSelectionOrWord(
  text: string,
  selStart: number,
  selEnd: number,
  map: (slice: string) => string,
): { text: string; start: number; end: number } {
  if (selStart !== selEnd) {
    const slice = text.slice(selStart, selEnd);
    const next = text.slice(0, selStart) + map(slice) + text.slice(selEnd);
    return { text: next, start: selStart, end: selStart + map(slice).length };
  }
  const wb = wordBoundsAt(text, selStart);
  if (!wb) return { text, start: selStart, end: selEnd };
  const slice = text.slice(wb.start, wb.end);
  const rep = map(slice);
  const next = text.slice(0, wb.start) + rep + text.slice(wb.end);
  return { text: next, start: wb.start, end: wb.start + rep.length };
}

export function duplicateLineAtCaret(text: string, caret: number): { text: string; caret: number } {
  const lineStart = text.lastIndexOf("\n", caret - 1) + 1;
  const nl = text.indexOf("\n", lineStart);
  if (nl === -1) {
    const line = text.slice(lineStart);
    const insert = (lineStart > 0 || text.length > 0 ? "\n" : "") + line;
    const next = text + insert;
    return { text: next, caret: text.length + insert.length };
  }
  const blockEnd = nl + 1;
  const block = text.slice(lineStart, blockEnd);
  const next = text.slice(0, blockEnd) + block + text.slice(blockEnd);
  return { text: next, caret: blockEnd + block.length };
}

export function trimTrailingWhitespace(text: string): string {
  return text
    .replace(/[ \t]+(\r?\n)/g, "$1")
    .replace(/[ \t]+$/, "");
}

export function insertAt(text: string, offset: number, insert: string): { text: string; caret: number } {
  const next = text.slice(0, offset) + insert + text.slice(offset);
  return { text: next, caret: offset + insert.length };
}

function caretLineAndCol(text: string, caret: number): { line: number; col: number } {
  const c = Math.min(Math.max(0, caret), text.length);
  const head = text.slice(0, c);
  const segs = head.split(/\r?\n/);
  const line = segs.length - 1;
  const col = segs[segs.length - 1]!.length;
  return { line, col };
}

function offsetAtLineCol(lines: readonly string[], line: number, col: number): number {
  let off = 0;
  for (let i = 0; i < line; i++) off += lines[i]!.length + 1;
  return off + Math.min(col, lines[line]!.length);
}

/** Move current line up/down (LF output; normalizes CR/CRLF splits). */
export function moveLine(text: string, caret: number, dir: -1 | 1): { text: string; caret: number } | null {
  const lines = text.split(/\r?\n/);
  if (lines.length < 2) return null;
  const { line: lineIdx, col } = caretLineAndCol(text, caret);
  if (dir === -1) {
    if (lineIdx <= 0) return null;
    const a = lines[lineIdx - 1]!;
    lines[lineIdx - 1] = lines[lineIdx]!;
    lines[lineIdx] = a;
    const newText = lines.join("\n");
    const newCaret = offsetAtLineCol(lines, lineIdx - 1, Math.min(col, lines[lineIdx - 1]!.length));
    return { text: newText, caret: newCaret };
  }
  if (lineIdx >= lines.length - 1) return null;
  const a = lines[lineIdx]!;
  lines[lineIdx] = lines[lineIdx + 1]!;
  lines[lineIdx + 1] = a;
  const newText = lines.join("\n");
  const newCaret = offsetAtLineCol(lines, lineIdx + 1, Math.min(col, lines[lineIdx + 1]!.length));
  return { text: newText, caret: newCaret };
}

/** Line block [start,end) for indent/sort/remove: full doc if selection empty. */
export function selectionLineBlockBounds(
  text: string,
  selStart: number,
  selEnd: number,
): { start: number; end: number } {
  const lo = Math.min(selStart, selEnd);
  const hi = Math.max(selStart, selEnd);
  if (lo === hi) return { start: 0, end: text.length };
  const blockStart = text.lastIndexOf("\n", Math.max(0, lo - 1)) + 1;
  const nl = text.indexOf("\n", Math.max(hi - 1, 0));
  const blockEnd = nl < 0 ? text.length : nl + 1;
  return { start: blockStart, end: blockEnd };
}

/** Join lines: selection merges newlines to spaces; no selection joins current line with next. */
export function joinLines(text: string, selStart: number, selEnd: number): { text: string; selStart: number; selEnd: number } | null {
  let lo = Math.min(selStart, selEnd);
  let hi = Math.max(selStart, selEnd);
  if (lo === hi) {
    const lineStart = text.lastIndexOf("\n", lo - 1) + 1;
    const nl = text.indexOf("\n", lineStart);
    if (nl < 0 || nl + 1 > text.length) return null;
    const secondStart = nl + 1;
    const nl2 = text.indexOf("\n", secondStart);
    lo = lineStart;
    hi = nl2 < 0 ? text.length : nl2;
    if (secondStart >= hi) return null;
  }
  const chunk = text.slice(lo, hi);
  if (!/[\r\n]/.test(chunk)) return null;
  const merged = chunk.replace(/\s*\r?\n\s*/g, " ").replace(/  +/g, " ");
  const next = text.slice(0, lo) + merged + text.slice(hi);
  return { text: next, selStart: lo, selEnd: lo + merged.length };
}

/** Drop lines that are empty or whitespace-only (selection or whole document). */
export function removeEmptyLines(text: string, selStart: number, selEnd: number): { text: string; selStart: number; selEnd: number } {
  const { start, end } = selectionLineBlockBounds(text, selStart, selEnd);
  const block = text.slice(start, end);
  const lines = block.split(/\r?\n/);
  const kept = lines.filter((ln) => ln.trim().length > 0);
  const out = kept.join("\n");
  const next = text.slice(0, start) + out + text.slice(end);
  return { text: next, selStart: start, selEnd: start + out.length };
}

/** Lexicographic sort (locale); selection or entire buffer when selection empty. */
export function sortLines(text: string, selStart: number, selEnd: number): { text: string; selStart: number; selEnd: number } {
  const { start, end } = selectionLineBlockBounds(text, selStart, selEnd);
  const block = text.slice(start, end);
  const lines = block.split(/\r?\n/);
  const trailNl = /\n$/.test(block) || /\r\n$/.test(block);
  const sorted = [...lines].sort((a, b) => a.localeCompare(b));
  let out = sorted.join("\n");
  if (trailNl && out.length > 0 && !out.endsWith("\n")) out += "\n";
  const next = text.slice(0, start) + out + text.slice(end);
  return { text: next, selStart: start, selEnd: start + out.length };
}

const SPACES4 = "    ";

const TAB_CH = "\t";

/** Add one tab at the start of each line in the block (or current line only if selection is empty). */
export function indentLinesWithTab(text: string, selStart: number, selEnd: number): { text: string; selStart: number; selEnd: number } {
  let { start, end } = selectionLineBlockBounds(text, selStart, selEnd);
  if (selStart === selEnd) {
    const c = selStart;
    const ls = text.lastIndexOf("\n", c - 1) + 1;
    const nl = text.indexOf("\n", c);
    const le = nl < 0 ? text.length : nl + 1;
    start = ls;
    end = le;
  }
  const block = text.slice(start, end);
  const lines = block.split(/\r?\n/);
  const out = lines.map((ln) => TAB_CH + ln).join("\n");
  const next = text.slice(0, start) + out + text.slice(end);
  return { text: next, selStart: start, selEnd: start + out.length };
}

/** Add four spaces at start of each line in block (or current line only if empty selection on one line). */
export function indentLines(text: string, selStart: number, selEnd: number): { text: string; selStart: number; selEnd: number } {
  let { start, end } = selectionLineBlockBounds(text, selStart, selEnd);
  if (selStart === selEnd) {
    const c = selStart;
    const ls = text.lastIndexOf("\n", c - 1) + 1;
    const nl = text.indexOf("\n", c);
    const le = nl < 0 ? text.length : nl + 1;
    start = ls;
    end = le;
  }
  const block = text.slice(start, end);
  const lines = block.split(/\r?\n/);
  const out = lines.map((ln) => SPACES4 + ln).join("\n");
  const next = text.slice(0, start) + out + text.slice(end);
  return { text: next, selStart: start, selEnd: start + out.length };
}

/** Remove one leading tab or up to four leading spaces per line. */
export function outdentLines(text: string, selStart: number, selEnd: number): { text: string; selStart: number; selEnd: number } {
  let { start, end } = selectionLineBlockBounds(text, selStart, selEnd);
  if (selStart === selEnd) {
    const c = selStart;
    const ls = text.lastIndexOf("\n", c - 1) + 1;
    const nl = text.indexOf("\n", c);
    const le = nl < 0 ? text.length : nl + 1;
    start = ls;
    end = le;
  }
  const block = text.slice(start, end);
  const lines = block.split(/\r?\n/);
  const out = lines.map((ln) => ln.replace(/^\t/, "").replace(/^ {1,4}/, "")).join("\n");
  const next = text.slice(0, start) + out + text.slice(end);
  return { text: next, selStart: start, selEnd: start + out.length };
}
